import "./notification_confirmation_item.less";
import React, { useState } from "react";

import { CheckOutlined } from "@ant-design/icons";
import { useSaveDataConfirmationMutation } from "@src/services/slices/projectsApi";
import {
  DataConfirmationNotification,
  DataConfirmationPayload,
} from "@src/types/notification_types";
import { Checkbox, Button, App, Form } from "antd";
import dayjs, { Dayjs } from "dayjs";
import _ from "lodash";

type DataConfirmationFormValue = Pick<
  DataConfirmationNotification,
  "digixDataConfirmed" | "resourcePlanDataConfirmed"
>;

type NotificationConfirmationItemProps = {
  dataConfirmation: DataConfirmationNotification;
  visibleUntil: string;
};

const NotificationConfirmationItem: React.FC<
  NotificationConfirmationItemProps
> = ({ dataConfirmation, visibleUntil }) => {
  const [saveDataConfirmationByProject] = useSaveDataConfirmationMutation();

  const { message } = App.useApp();
  const [buttonDisabled, setButtonDisabled] = useState(true);

  const {
    resourcePlanDataConfirmed,
    digixDataConfirmed,
    projectId,
    projectName,
  } = dataConfirmation;
  const [form] = Form.useForm<DataConfirmationFormValue>();

  async function onConfirm() {
    const formValues: DataConfirmationFormValue = form.getFieldsValue();

    const newConfirmedData: DataConfirmationPayload = _.omitBy(
      formValues,
      (value: boolean, key: string) => _.isEqual(value, dataConfirmation[key])
    );

    saveDataConfirmationByProject({ projectId: projectId, ...newConfirmedData })
      .unwrap()
      .then(() => {
        const successMessage = (
          <div>
            Data confirmed for <b>{projectName}</b>
          </div>
        );
        setButtonDisabled(true);
        message.success(successMessage);
      })
      .catch((error) => console.log(error));
  }

  function onChange() {
    const updatedConfirmations: DataConfirmationFormValue =
      form.getFieldsValue();

    const isButtonDisabled: boolean = _.isEqual(updatedConfirmations, {
      digixDataConfirmed: digixDataConfirmed,
      resourcePlanDataConfirmed: resourcePlanDataConfirmed,
    });

    setButtonDisabled(isButtonDisabled);
  }

  function getLeftDays(): number {
    const date: Dayjs = dayjs(visibleUntil);
    return date.diff(dayjs(), "day");
  }

  return (
    <div className="notification_confirmation_item__content">
      <div
        className="notification_confirmation_item__header"
        data-testid="project-name"
      >
        {projectName}
      </div>
      <div>
        <Form
          form={form}
          autoComplete="off"
          size="large"
          onValuesChange={onChange}
          initialValues={{
            resourcePlanDataConfirmed: resourcePlanDataConfirmed,
            digixDataConfirmed: digixDataConfirmed,
          }}
        >
          <Form.Item
            name="resourcePlanDataConfirmed"
            valuePropName="checked"
            noStyle
          >
            <Checkbox disabled={resourcePlanDataConfirmed}>
              Resource plan confirmed
            </Checkbox>
          </Form.Item>
          <Form.Item name="digixDataConfirmed" valuePropName="checked" noStyle>
            <Checkbox disabled={digixDataConfirmed}>
              Project Details aligned with DigiX
            </Checkbox>
          </Form.Item>
        </Form>
      </div>
      <div className="notification_confirmation_item__button-section">
        <Button
          type="primary"
          data-testid="data-confirmation-button"
          icon={<CheckOutlined />}
          onClick={() => onConfirm()}
          disabled={buttonDisabled}
        >
          Confirm Changes
        </Button>
      </div>
      <div className="notification_confirmation_item__footer">
        <span className="notification_confirmation_item__footer date">
          {dayjs(visibleUntil).format("DD/MM/YYYY")}
        </span>
        <span className="notification_confirmation_item__footer left-days">
          {getLeftDays()} days left
        </span>
      </div>
    </div>
  );
};

export default NotificationConfirmationItem;
